/**
 * useActionCounts — sidebar badge for the admin AdminSidebar.
 *
 * Uses `GET /actions?counts_only=true` to get the current authorised
 * counts with items=[]. `badge` = critical + today + overdue (never
 * inflated by upcoming or info). Silent on failure so the sidebar never
 * blocks or shows an error banner if the endpoint is unreachable.
 *
 * Refresh triggers:
 *   - Load on mount.
 *   - `reload()` (manual bump — same version-bump pattern as
 *     useFollowUpCounts).
 *   - `window` event `"dolce:actions-updated"` — dispatched by ActionCard
 *     right after a mutation returns 2xx. Lets the sidebar badge stay in
 *     sync with the board without a Context/Redux and without polling.
 *     Failed mutations do NOT dispatch — badge stays put.
 */
import { useCallback, useEffect, useState } from "react";
import { fetchActionCounts } from "@/lib/actionsApi";

export const ACTIONS_UPDATED_EVENT = "dolce:actions-updated";

export function useActionCounts({ enabled = true } = {}) {
  const [counts, setCounts] = useState(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const load = () => {
      fetchActionCounts()
        .then((c) => {
          if (!cancelled) setCounts(c || null);
        })
        .catch(() => {
          if (!cancelled) setCounts(null);
        });
    };
    load();
    const handler = () => load();
    window.addEventListener(ACTIONS_UPDATED_EVENT, handler);
    return () => {
      cancelled = true;
      window.removeEventListener(ACTIONS_UPDATED_EVENT, handler);
    };
  }, [enabled, version]);

  const badge = counts
    ? (counts.critical || 0) + (counts.today || 0) + (counts.overdue || 0)
    : 0;

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return { counts, badge, reload };
}
